/**
 * See360 360° Viewer Engine
 * Main viewer class: drag/swipe to spin, autoplay, fullscreen, mobile touch support.
 */

import { Preloader, type PreloaderOptions } from './Preloader';
import { HotspotManager, type HotspotData } from './HotspotManager';
import { Analytics, type AnalyticsConfig } from './Analytics';
import { PanoramaViewer, type PanoramaHotspotData } from './PanoramaViewer';
import { Gallery, type GalleryImage } from './Gallery';

export interface ViewerConfig {
  container: HTMLElement;
  cdnBaseUrl: string;
  cdnBasePath: string;
  imagePattern: string;
  frameCount: number;
  hotspots?: HotspotData[];
  interiorPath?: string | null;
  interiorFrameCount?: number;
  interiorImagePattern?: string;
  interiorStatus?: string;
  interiorType?: string;        // 'FRAMES' | 'PANORAMA'
  apiUrl?: string;              // API base URL for panorama proxy
  vehicleId?: string;           // Vehicle ID for panorama proxy URL
  autoplay?: boolean;
  autoplaySpeed?: number;       // ms per frame, default 200
  analytics?: AnalyticsConfig;
  gallery?: GalleryImage[];      // Gallery images for thumbnail ribbon
  galleryThumbWidth?: number;    // Thumbnail width in px (default: 80)
  galleryThumbHeight?: number;   // Thumbnail height in px (default: 60)
  galleryBgColor?: string;       // Ribbon background color (default: '#0a0a0a')
  onReady?: () => void;
  onFrameChange?: (frameIndex: number) => void;
}

export class Viewer {
  private config: ViewerConfig;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private preloader: Preloader;
  private hotspotManager: HotspotManager;
  private analytics: Analytics;
  private images: HTMLImageElement[] = [];
  private sparseImages: (HTMLImageElement | null)[] = [];  // Progressive: includes nulls for unloaded frames
  private interiorImages: HTMLImageElement[] = [];
  private currentFrame = 0;
  private allFramesLoaded = false;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartFrame = 0;
  private autoplayInterval: number | null = null;
  private isFullscreen = false;
  private progressBar: HTMLDivElement | null = null;
  private controlsContainer: HTMLDivElement | null = null;
  private viewMode: 'EXTERIOR' | 'INTERIOR' = 'EXTERIOR';
  private interiorPreloader: Preloader | null = null;
  private interiorLoaded = false;
  private exteriorBtn: HTMLButtonElement | null = null;
  private interiorBtn: HTMLButtonElement | null = null;
  private panoramaViewer: PanoramaViewer | null = null;
  private isPanoramaMode = false;
  private gallery: Gallery | null = null;

  constructor(config: ViewerConfig) {
    this.config = config;

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.objectFit = 'contain';
    this.canvas.style.cursor = 'grab';
    this.canvas.style.touchAction = 'none';
    this.canvas.style.position = 'relative';
    this.canvas.style.zIndex = '1'; // Below hotspot overlays (z-index: 30)

    // Create context
    this.ctx = this.canvas.getContext('2d')!;

    // Initialize sub-systems
    this.preloader = new Preloader({
      cdnBaseUrl: config.cdnBaseUrl,
      cdnBasePath: config.cdnBasePath,
      imagePattern: config.imagePattern,
      frameCount: config.frameCount,
      containerWidth: config.container.clientWidth,
      onProgress: (loaded, total) => this.updateProgressBar(loaded, total),
      onKeyFramesLoaded: (sparseImages) => this.onKeyFramesReady(sparseImages),
      onComplete: (images) => this.onAllFramesLoaded(images),
    });

    this.analytics = new Analytics(config.analytics || { apiUrl: '', apiKey: '', spin360Id: '' });

    this.setup();

    // Filter hotspots: only exterior hotspots go to HotspotManager
    // Canvas z-index: 1, hotspot wrappers z-index: 30 — stacking is correct
    const allHotspots = config.hotspots || [];
    const exteriorHotspots = allHotspots.filter(h => !h.viewType || h.viewType === 'EXTERIOR');

    this.hotspotManager = new HotspotManager({
      container: config.container,
      hotspots: exteriorHotspots,
      frameCount: config.frameCount,
      onHotspotClick: (hotspot) => {
        this.analytics.track('HOTSPOT_CLICK', { frameIndex: this.currentFrame, hotspotId: hotspot.id });
      },
    });

    // Initialize gallery if images provided
    if (config.gallery && config.gallery.length > 0) {
      // Build a thumbnail URL from exterior frame 4 (~30° front 3/4 view)
      const thumbFrameIndex = Math.min(4, config.frameCount);
      const thumbFramePadded = String(thumbFrameIndex).padStart(3, '0');
      const spinThumbnailUrl = `${config.cdnBaseUrl}/${config.cdnBasePath}${config.imagePattern.replace('{index}', thumbFramePadded)}`;

      this.gallery = new Gallery({
        container: config.container,
        images: config.gallery,
        spinThumbnailUrl,
        thumbWidth: config.galleryThumbWidth,
        thumbHeight: config.galleryThumbHeight,
        bgColor: config.galleryBgColor,
        onImageSelect: (index) => {
          // Pause autoplay when gallery image is selected
          this.stopAutoplay();
          this.analytics.track('GALLERY_VIEW', { frameIndex: index });
        },
      });
    }
  }

  private setup(): void {
    const { container } = this.config;

    // Style container
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.style.userSelect = 'none';
    container.style.backgroundColor = '#000';

    // Add canvas
    container.appendChild(this.canvas);

    // Create progress bar
    this.createProgressBar();

    // Create controls
    this.createControls();

    // Bind events
    this.bindMouseEvents();
    this.bindTouchEvents();
    this.bindKeyboardEvents();

    // Start loading
    this.preloader.load();
  }

  private createProgressBar(): void {
    this.progressBar = document.createElement('div');
    this.progressBar.style.cssText = `
      position: absolute; bottom: 0; left: 0; width: 0%;
      height: 4px; background: linear-gradient(90deg, #00d4ff, #7b2fff);
      transition: width 0.3s ease; z-index: 10; border-radius: 2px;
    `;
    this.config.container.appendChild(this.progressBar);
  }

  private updateProgressBar(loaded: number, total: number): void {
    if (this.progressBar) {
      this.progressBar.style.width = `${(loaded / total) * 100}%`;
    }
  }

  private createControls(): void {
    // ── Top control bar ──
    this.controlsContainer = document.createElement('div');
    this.controlsContainer.style.cssText = `
      position: absolute; top: 12px; left: 12px; right: 12px;
      display: flex; justify-content: space-between; align-items: center;
      z-index: 20; pointer-events: none;
    `;

    // ── Left side: View mode tabs (only if interior available) ──
    const leftGroup = document.createElement('div');
    leftGroup.style.cssText = `
      display: flex; gap: 4px; background: rgba(0,0,0,0.5);
      border-radius: 10px; padding: 3px; backdrop-filter: blur(8px);
      pointer-events: auto;
    `;

    const hasInterior = (
      this.config.interiorStatus === 'COMPLETE' && (
        // Frame-based interior
        (this.config.interiorPath && this.config.interiorFrameCount && this.config.interiorFrameCount > 0) ||
        // Panorama interior
        (this.config.interiorType === 'PANORAMA')
      )
    );

    if (hasInterior) {
      // Exterior tab
      this.exteriorBtn = document.createElement('button');
      this.exteriorBtn.textContent = '🚤 Exterior';
      this.exteriorBtn.style.cssText = this.getTabStyle(true);
      this.exteriorBtn.addEventListener('click', () => this.switchToMode('EXTERIOR'));
      leftGroup.appendChild(this.exteriorBtn);

      // Interior tab
      this.interiorBtn = document.createElement('button');
      this.interiorBtn.textContent = '⚓ Interior';
      this.interiorBtn.style.cssText = this.getTabStyle(false);
      this.interiorBtn.addEventListener('click', () => this.switchToMode('INTERIOR'));
      leftGroup.appendChild(this.interiorBtn);
    }

    this.controlsContainer.appendChild(leftGroup);

    // ── Right side: Autoplay + Fullscreen ──
    const rightGroup = document.createElement('div');
    rightGroup.style.cssText = `
      display: flex; gap: 8px; pointer-events: auto;
    `;

    // Autoplay button
    const autoplayBtn = this.createPillButton('▶ Autoplay', 'Toggle autoplay');
    autoplayBtn.addEventListener('click', () => {
      this.toggleAutoplay();
      const isPlaying = this.isPanoramaMode && this.panoramaViewer
        ? this.panoramaViewer.isAutoRotating()
        : this.autoplayInterval !== null;
      autoplayBtn.textContent = isPlaying ? '⏸ Pause' : '▶ Autoplay';
    });
    rightGroup.appendChild(autoplayBtn);

    // Fullscreen button
    const fullscreenBtn = this.createPillButton('⛶ Fullscreen', 'Toggle fullscreen');
    fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
    rightGroup.appendChild(fullscreenBtn);

    this.controlsContainer.appendChild(rightGroup);
    this.config.container.appendChild(this.controlsContainer);
  }

  private getTabStyle(active: boolean): string {
    return `
      padding: 8px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
      border: none; cursor: pointer; transition: all 0.2s;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: ${active ? 'linear-gradient(135deg, #00d4ff, #7b2fff)' : 'transparent'};
      color: ${active ? '#fff' : 'rgba(255,255,255,0.6)'};
    `;
  }

  private switchToMode(mode: 'EXTERIOR' | 'INTERIOR'): void {
    if (mode === this.viewMode) return;
    this.viewMode = mode;
    this.stopAutoplay();
    this.currentFrame = 0;

    // Update tab button styles
    if (this.exteriorBtn) this.exteriorBtn.style.cssText = this.getTabStyle(mode === 'EXTERIOR');
    if (this.interiorBtn) this.interiorBtn.style.cssText = this.getTabStyle(mode === 'INTERIOR');

    if (mode === 'INTERIOR') {
      this.analytics.track('INTERIOR_OPEN', { frameIndex: this.currentFrame });

      // Hide canvas (panorama uses its own renderer)
      if (this.config.interiorType === 'PANORAMA') {
        this.isPanoramaMode = true;
        this.canvas.style.visibility = 'hidden'; // Keep layout, hide visually
        this.showPanorama();
      } else if (this.interiorLoaded && this.interiorImages.length > 0) {
        // Already loaded — resize canvas, render, and start autoplay
        if (this.interiorImages[0]) {
          this.canvas.width = this.interiorImages[0].naturalWidth;
          this.canvas.height = this.interiorImages[0].naturalHeight;
        }
        this.renderFrame(0);
        this.startAutoplay();
      } else {
        // Load interior frames
        this.loadInteriorFrames();
      }
    } else {
      // Switch back to exterior
      this.analytics.track('EXTERIOR_OPEN', { frameIndex: this.currentFrame });
      if (this.isPanoramaMode) {
        this.isPanoramaMode = false;
        this.destroyPanorama();
        this.canvas.style.visibility = ''; // Restore visibility
      }
      // Restore exterior canvas size
      if (this.images[0]) {
        this.canvas.width = this.images[0].naturalWidth;
        this.canvas.height = this.images[0].naturalHeight;
      }
      this.renderFrame(0);
      this.startAutoplay();
    }
  }

  private loadInteriorFrames(): void {
    if (!this.config.interiorPath || !this.config.interiorFrameCount || !this.config.interiorImagePattern) return;

    // Show loading progress
    this.createProgressBar();

    this.interiorPreloader = new Preloader({
      cdnBaseUrl: this.config.cdnBaseUrl,
      cdnBasePath: this.config.interiorPath,
      imagePattern: this.config.interiorImagePattern,
      frameCount: this.config.interiorFrameCount,
      onProgress: (loaded, total) => this.updateProgressBar(loaded, total),
      onComplete: (images) => {
        this.interiorImages = images;
        this.interiorLoaded = true;

        // Remove progress bar
        if (this.progressBar) {
          this.progressBar.style.opacity = '0';
          setTimeout(() => this.progressBar?.remove(), 300);
          this.progressBar = null;
        }

        // Set canvas size and render first interior frame
        if (images.length > 0) {
          this.canvas.width = images[0].naturalWidth;
          this.canvas.height = images[0].naturalHeight;
        }
        this.renderFrame(0);

        // Start autoplay for interior frames
        this.startAutoplay();
      },
    });

    this.interiorPreloader.load();
  }

  private createPillButton(text: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    btn.title = title;
    btn.style.cssText = `
      padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
      border: 1px solid rgba(255,255,255,0.15); cursor: pointer;
      background: rgba(0,0,0,0.5); color: rgba(255,255,255,0.8);
      backdrop-filter: blur(8px); transition: all 0.2s;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(0,0,0,0.7)'; btn.style.color = '#fff'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'rgba(0,0,0,0.5)'; btn.style.color = 'rgba(255,255,255,0.8)'; });
    return btn;
  }

  /**
   * Called when key frames (first 6) are loaded — viewer becomes interactive immediately.
   */
  private onKeyFramesReady(sparseImages: (HTMLImageElement | null)[]): void {
    this.sparseImages = sparseImages;
    // Build dense array from available frames for the images reference
    this.images = sparseImages.filter((img): img is HTMLImageElement => img !== null);

    // Remove progress bar
    if (this.progressBar) {
      this.progressBar.style.opacity = '0';
      setTimeout(() => this.progressBar?.remove(), 300);
    }

    // Set canvas size from the first available image
    const firstImg = sparseImages.find((img): img is HTMLImageElement => img !== null);
    if (firstImg) {
      this.canvas.width = firstImg.naturalWidth;
      this.canvas.height = firstImg.naturalHeight;
    }

    // Render first frame
    this.renderFrame(0);

    // Start autoplay if enabled — already interactive with key frames!
    if (this.config.autoplay !== false) {
      this.startAutoplay();
    }

    // Track view event
    this.analytics.track('VIEW', { frameIndex: 0 });

    // Notify ready
    this.config.onReady?.();
  }

  /**
   * Called when ALL frames have finished loading in the background.
   */
  private onAllFramesLoaded(images: HTMLImageElement[]): void {
    this.images = images;
    this.allFramesLoaded = true;
    // Sparse array is now fully populated — rendering uses exact frames from here on
    this.sparseImages = this.preloader.getSparseImages();
  }

  private getActiveImages(): HTMLImageElement[] {
    return this.viewMode === 'INTERIOR' ? this.interiorImages : this.images;
  }

  private getActiveSparseImages(): (HTMLImageElement | null)[] {
    return this.viewMode === 'INTERIOR' ? this.interiorImages : this.sparseImages;
  }

  private getActiveFrameCount(): number {
    return this.viewMode === 'INTERIOR'
      ? (this.config.interiorFrameCount || 0)
      : this.config.frameCount;
  }

  /**
   * Find the nearest available frame in the sparse image array.
   * During progressive loading, not all frames are loaded yet.
   */
  private findNearestFrame(index: number): { img: HTMLImageElement; actualIndex: number } | null {
    const sparse = this.getActiveSparseImages();
    const count = this.getActiveFrameCount();
    if (count === 0) return null;

    // Exact frame available?
    if (sparse[index]) return { img: sparse[index]!, actualIndex: index };

    // Search outward from the target index (wrapping)
    for (let offset = 1; offset <= Math.ceil(count / 2); offset++) {
      const after = (index + offset) % count;
      if (sparse[after]) return { img: sparse[after]!, actualIndex: after };
      const before = (index - offset + count) % count;
      if (sparse[before]) return { img: sparse[before]!, actualIndex: before };
    }

    return null;
  }

  private renderFrame(index: number): void {
    const frameCount = this.getActiveFrameCount();
    if (index < 0 || frameCount === 0) return;

    this.currentFrame = index;

    // Find the best available frame to render
    const result = this.findNearestFrame(index);
    if (!result) return;

    const { img } = result;

    // Clear and draw
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);

    // Update hotspots for this frame (exterior only)
    if (this.viewMode === 'EXTERIOR') {
      this.hotspotManager.showForFrame(index);
    } else {
      this.hotspotManager.showForFrame(-1); // Hide all hotspots in interior
    }

    // Notify
    this.config.onFrameChange?.(index);
  }

  // ─── Mouse Events ─────────────────────────────────────

  private bindMouseEvents(): void {
    this.canvas.addEventListener('mousedown', (e) => this.onDragStart(e.clientX));
    window.addEventListener('mousemove', (e) => this.onDragMove(e.clientX));
    window.addEventListener('mouseup', () => this.onDragEnd());
  }

  // ─── Touch Events ─────────────────────────────────────

  private bindTouchEvents(): void {
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        this.onDragStart(e.touches[0].clientX);
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        this.onDragMove(e.touches[0].clientX);
      }
      // TODO: Pinch to zoom with two fingers
    }, { passive: false });

    this.canvas.addEventListener('touchend', () => this.onDragEnd());
  }

  // ─── Keyboard Events ──────────────────────────────────

  private bindKeyboardEvents(): void {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prevFrame();
      if (e.key === 'ArrowRight') this.nextFrame();
      if (e.key === 'f' || e.key === 'F') this.toggleFullscreen();
      if (e.key === ' ') { e.preventDefault(); this.toggleAutoplay(); }
    });
  }

  // ─── Drag Handling ─────────────────────────────────────

  private onDragStart(x: number): void {
    this.isDragging = true;
    this.dragStartX = x;
    this.dragStartFrame = this.currentFrame;
    this.stopAutoplay();
    this.canvas.style.cursor = 'grabbing';
  }

  private onDragMove(x: number): void {
    if (!this.isDragging) return;

    const frameCount = this.getActiveFrameCount();
    if (frameCount === 0) return;
    const delta = x - this.dragStartX;
    const sensitivity = this.canvas.clientWidth / frameCount;
    const frameDelta = Math.round(delta / sensitivity);
    let newFrame = (this.dragStartFrame - frameDelta) % frameCount;
    if (newFrame < 0) newFrame += frameCount;

    if (newFrame !== this.currentFrame) {
      this.renderFrame(newFrame);
      this.analytics.track('SPIN', { frameIndex: newFrame });
    }
  }

  private onDragEnd(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.canvas.style.cursor = 'grab';
  }

  // ─── Navigation ────────────────────────────────────────

  nextFrame(): void {
    const fc = this.getActiveFrameCount();
    if (fc === 0) return;
    const next = (this.currentFrame + 1) % fc;
    this.renderFrame(next);
  }

  prevFrame(): void {
    const fc = this.getActiveFrameCount();
    if (fc === 0) return;
    let prev = this.currentFrame - 1;
    if (prev < 0) prev = fc - 1;
    this.renderFrame(prev);
  }

  goToFrame(index: number): void {
    const fc = this.getActiveFrameCount();
    if (fc === 0) return;
    this.renderFrame(index % fc);
  }

  // ─── Autoplay ──────────────────────────────────────────

  startAutoplay(): void {
    if (this.isPanoramaMode && this.panoramaViewer) {
      this.panoramaViewer.startAutoRotate();
      return;
    }
    this.stopAutoplay();
    const speed = this.config.autoplaySpeed || 200;
    this.autoplayInterval = window.setInterval(() => this.nextFrame(), speed);
  }

  stopAutoplay(): void {
    if (this.autoplayInterval !== null) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    if (this.isPanoramaMode && this.panoramaViewer) {
      this.panoramaViewer.stopAutoRotate();
    }
  }

  toggleAutoplay(): void {
    if (this.isPanoramaMode && this.panoramaViewer) {
      this.panoramaViewer.toggleAutoRotate();
      return;
    }
    if (this.autoplayInterval !== null) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  // ─── Fullscreen ────────────────────────────────────────

  async toggleFullscreen(): Promise<void> {
    const container = this.config.container;

    if (!this.isFullscreen) {
      try {
        await container.requestFullscreen();
        this.isFullscreen = true;
        this.analytics.track('FULLSCREEN', { frameIndex: this.currentFrame });
      } catch {
        console.warn('Fullscreen not supported');
      }
    } else {
      await document.exitFullscreen();
      this.isFullscreen = false;
    }
  }

  // ─── Public API ────────────────────────────────────────

  getCurrentFrame(): number {
    return this.currentFrame;
  }

  getFrameCount(): number {
    return this.config.frameCount;
  }

  destroy(): void {
    this.stopAutoplay();
    this.hotspotManager.destroy();
    this.destroyPanorama();
    this.config.container.innerHTML = '';
  }

  // ─── Panorama Viewer ───────────────────────────────────

  private showPanorama(): void {
    this.destroyPanorama();

    // Build panorama image URL via API proxy
    const apiUrl = this.config.apiUrl || this.config.analytics?.apiUrl || 'https://api.see360.ai';
    const vehicleId = this.config.vehicleId || '';
    const imageUrl = `${apiUrl}/vehicles/${vehicleId}/panorama`;

    // Create a container for the panorama (same size as canvas)
    const panoramaContainer = document.createElement('div');
    panoramaContainer.style.cssText = 'width: 100%; height: 100%; position: absolute; top: 0; left: 0; z-index: 2;';
    panoramaContainer.id = 'see360-panorama-container';
    this.config.container.appendChild(panoramaContainer);

    // Create hint overlay
    const hint = document.createElement('div');
    hint.style.cssText = `
      position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%);
      background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
      padding: 6px 14px; border-radius: 16px; font-size: 11px;
      color: rgba(255,255,255,0.5); pointer-events: none; z-index: 5;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    hint.textContent = '🔮 Drag to look around · Scroll to zoom';
    this.config.container.appendChild(hint);

    // Build panorama hotspots from interior hotspot data
    // Interior hotspots store lon/lat in xPercent/yPercent fields
    const allHotspots = this.config.hotspots || [];
    const interiorHotspots: PanoramaHotspotData[] = allHotspots
      .filter(h => h.viewType === 'INTERIOR')
      .map(h => ({
        id: h.id,
        lon: h.xPercent,   // xPercent stores longitude for panorama hotspots
        lat: h.yPercent,   // yPercent stores latitude for panorama hotspots
        label: h.label,
        description: h.description,
        category: h.category,
      }));

    this.panoramaViewer = new PanoramaViewer({
      container: panoramaContainer,
      imageUrl,
      hotspots: interiorHotspots,
      onReady: () => {
        console.log('[See360] Panorama viewer ready');
      },
      onError: (error: string) => {
        console.error('[See360] Panorama error:', error);
      },
    });
  }

  private destroyPanorama(): void {
    if (this.panoramaViewer) {
      this.panoramaViewer.destroy();
      this.panoramaViewer = null;
    }
    // Remove panorama container and hint
    const pc = this.config.container.querySelector('#see360-panorama-container');
    pc?.remove();
    // Remove hint elements (any extra divs we added)
    const hints = this.config.container.querySelectorAll('div[style*="pointer-events: none"]');
    hints.forEach(h => {
      if (h.textContent?.includes('Drag to look')) h.remove();
    });
  }
}
